import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth-context";
import { useTheme } from "@/lib/theme-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import PushSettings from "@/components/PushSettings";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";

const LANGS = [
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi" },
  { code: "kn", name: "Kannada" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "ml", name: "Malayalam" },
  { code: "mr", name: "Marathi" },
  { code: "bn", name: "Bengali" },
  { code: "ar", name: "Arabic" },
  { code: "fr", name: "French" },
  { code: "es", name: "Spanish" },
];

export default function Settings() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [lang, setLang] = useState(user?.language || "en");
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quota");
        setQuota(data);
      } catch (e) {
        logQuietError("settings.quota.load", e);
      }
    })();
  }, []);

  const initials = (user?.name || "T").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Layout title="Settings">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="tb-card p-6" data-testid="profile-card">
            <h3 className="font-display text-lg font-medium">Profile</h3>
            <div className="mt-4 flex items-center gap-4">
              <Avatar className="h-16 w-16">
                {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
                <AvatarFallback className="bg-violet-500/15 text-violet-700 dark:text-violet-200 text-base">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-display text-lg font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
                  {user?.auth_provider === "google" ? "Google account" : "Email account"}
                </p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Display name</Label>
                <Input className="mt-1.5 h-11 rounded-xl" defaultValue={user?.name} disabled />
              </div>
              <div>
                <Label>Institution</Label>
                <Input className="mt-1.5 h-11 rounded-xl" defaultValue={user?.institution || ""} disabled />
              </div>
            </div>
          </div>

          <div className="tb-card p-6" data-testid="prefs-card">
            <h3 className="font-display text-lg font-medium">Preferences</h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Dark mode</p>
                  <p className="text-xs text-muted-foreground">Toggle theme across the app.</p>
                </div>
                <Switch
                  checked={theme === "dark"}
                  onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
                  data-testid="settings-theme-switch"
                />
              </div>

              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Preferred language</p>
                  <p className="text-xs text-muted-foreground">Used as default for AI generations.</p>
                </div>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger className="w-44 h-10 rounded-xl" data-testid="settings-lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t border-border pt-4">
                <PushSettings />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="tb-card p-6" data-testid="quota-card">
            <h3 className="font-display text-lg font-medium">AI usage today</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Daily soft quota of <span className="font-medium text-foreground">{quota?.limit ?? 200}</span> AI calls per day.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {Object.entries(quota?.by_kind || {}).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-border p-3">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{k.replace(/_/g, " ")}</p>
                  <p className="font-display text-lg font-medium mt-1">{v}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Total today: {quota?.total ?? 0}. Resets at 00:00 UTC.
            </p>
          </div>

          <div className="tb-card p-6">
            <h3 className="font-display text-lg font-medium">Plan</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              You're on the <span className="text-foreground font-medium">Beta · Free</span> plan.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>✦ Unlimited AI generations</li>
              <li>✦ TeeBee Vision OCR for handwriting, PDFs & whiteboards</li>
              <li>✦ All 11+ languages</li>
              <li>✦ Smart attendance & analytics</li>
            </ul>
          </div>

          <div className="tb-card p-6">
            <h3 className="font-display text-lg font-medium text-destructive">Sign out</h3>
            <p className="mt-2 text-sm text-muted-foreground">End your session on this device.</p>
            <Button
              onClick={logout}
              variant="outline"
              className="mt-4 w-full rounded-full"
              data-testid="settings-logout"
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
